import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Card } from '../components/ui/Card';
import toast from 'react-hot-toast';
import Papa from 'papaparse';
import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { debounce } from '../utils/debounce';
import { escapeIlikeToken, isLikelyUuid, matchesAllTokens, normalizeSearchInput, tokenize } from '../utils/tableSearch';

export default function PatientsPage() {
  const queryClient = useQueryClient();
  const [csvPatients, setCsvPatients] = useState<any[]>([]);
  const [syncingFrom4D, setSyncingFrom4D] = useState(false);
  const [syncingToZoneDM, setSyncingToZoneDM] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [selectedPatient, setSelectedPatient] = useState<any | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skipSynced, setSkipSynced] = useState(true); // Default: skip already-synced items (checked by default)
  const [confirmSyncFrom4DOpen, setConfirmSyncFrom4DOpen] = useState(false);
  const [skipSyncedFrom4D, setSkipSyncedFrom4D] = useState(false); // Default: false (process all)
  const [dateFilter, setDateFilter] = useState<'all' | 'recent'>('recent'); // Default: show recent (last 3 months)
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest'); // Default: newest first
  const [syncStatusFilter, setSyncStatusFilter] = useState<string>('all'); // Default: show all statuses

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value: string) => {
        setSearchTerm(normalizeSearchInput(value));
        setPage(1);
      }, 250),
    []
  );

  useEffect(() => {
    debouncedSetSearchTerm(searchInput);
  }, [searchInput, debouncedSetSearchTerm]);

  // Total count (not limited by default 1000 row response)
  const { data: patientsTotalCount = 0 } = useQuery({
    queryKey: ['patients-total-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('patients')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id);
      if (error) throw error;
      return count || 0;
    },
  });

  // Synced count (zonedm_synced status)
  const { data: patientsSyncedCount = 0 } = useQuery({
    queryKey: ['patients-synced-count'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const { count, error } = await supabase
        .from('patients')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .eq('zonedm_sync_status', 'zonedm_synced');
      if (error) throw error;
      return count || 0;
    },
  });

  // Fetch patients from Supabase with server-side pagination and filtering
  const { data: patients, isLoading } = useQuery({
    queryKey: ['patients', page, rowsPerPage, searchTerm, dateFilter, sortOrder, syncStatusFilter],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      
      // Calculate pagination range
      const from = (page - 1) * rowsPerPage;
      const to = from + rowsPerPage - 1;
      
      const buildBaseQuery = () => {
        let baseQuery = supabase
          .from('patients')
          .select('*', { count: 'exact' })
          .eq('user_id', session.user.id);

        // Date filter: show only records updated in last 3 months if 'recent' is selected
        if (dateFilter === 'recent') {
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          baseQuery = baseQuery.gte('external_updated_at', threeMonthsAgo.toISOString());
        }

        // Sync status filter: filter by zonedm_sync_status
        if (syncStatusFilter !== 'all') {
          if (syncStatusFilter === 'empty') {
            // Filter for null or empty string values - use PostgREST format
            baseQuery = baseQuery.or('zonedm_sync_status.is.null,zonedm_sync_status.eq.');
          } else {
            baseQuery = baseQuery.eq('zonedm_sync_status', syncStatusFilter);
          }
        }

        return baseQuery;
      };

      const normalizedSearch = normalizeSearchInput(searchTerm);
      const searchTokens = tokenize(normalizedSearch);

      if (searchTokens.length > 0) {
        const escapedTokens = searchTokens.map(escapeIlikeToken).filter(Boolean);
        const searchConditions: string[] = [];

        escapedTokens.forEach((escapedToken, index) => {
          const rawToken = searchTokens[index];
          searchConditions.push(`last_name.ilike.%${escapedToken}%`);
          searchConditions.push(`first_name.ilike.%${escapedToken}%`);
          searchConditions.push(`email.ilike.%${escapedToken}%`);
          searchConditions.push(`phone_primary.ilike.%${escapedToken}%`);
          searchConditions.push(`status.ilike.%${escapedToken}%`);
          searchConditions.push(`zonedm_sync_status.ilike.%${escapedToken}%`);
          searchConditions.push(`account_number.ilike.%${escapedToken}%`);
          searchConditions.push(`zonedm_contact_id.ilike.%${escapedToken}%`);
          searchConditions.push(`city.ilike.%${escapedToken}%`);
          searchConditions.push(`state.ilike.%${escapedToken}%`);

          if (/^\d+$/.test(rawToken)) {
            searchConditions.push(`external_id.eq.${rawToken}`);
            searchConditions.push(`coordinator_id.eq.${rawToken}`);
            searchConditions.push(`profile_photo_id.eq.${rawToken}`);
          }

          if (isLikelyUuid(rawToken)) {
            searchConditions.push(`id.eq.${rawToken}`);
            searchConditions.push(`user_id.eq.${rawToken}`);
          }
        });

        let filteredData: any[] = [];
        if (searchConditions.length > 0) {
          const searchQuery = buildBaseQuery().or(searchConditions.join(','));
          const { data: coarseData, error: searchError } = await searchQuery.order('external_updated_at', { ascending: sortOrder === 'oldest' });
          if (searchError) {
            console.error('Patient search error:', searchError);
            throw searchError;
          }

          filteredData = (coarseData || []).filter((patient: any) =>
            matchesAllTokens([
              patient.id,
              patient.user_id,
              patient.external_id,
              patient.first_name,
              patient.last_name,
              patient.email,
              patient.phone_primary,
              patient.status,
              patient.zonedm_sync_status,
              patient.account_number,
              patient.zonedm_contact_id,
              patient.coordinator_id,
              patient.profile_photo_id,
              patient.address1,
              patient.city,
              patient.state,
              patient.zipcode,
            ], searchTokens)
          );
        }

        return {
          data: filteredData.slice(from, to + 1),
          count: filteredData.length,
        };
      }

      const { data, error, count } = await buildBaseQuery()
        .order('external_updated_at', { ascending: sortOrder === 'oldest' })
        .range(from, to);
      
      if (error) {
        console.error('Patient search error:', error);
        throw error;
      }
      return { data: data || [], count: count || 0 };
    },
  });

  // Extract data and count from query result
  const patientsData = patients?.data || [];
  const patientsCount = patients?.count || 0;
  const totalPages = Math.max(1, Math.ceil(patientsCount / rowsPerPage));

  // CSV upload handler
  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse<any>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        setCsvPatients(results.data as any[]);
        toast.success('CSV parsed!');
      },
      error: (error: Error, file: File) => toast.error('CSV parse error: ' + error.message),
    });
  };

  // Sync FROM 4D using the new fetch → process loop (staged batches)
  const syncFrom4D = async (skipSyncedOption: boolean = false) => {
    setSyncingFrom4D(true);
    const progressToastId = 'patients-progress';
    try {
      // Calculate initial skip if skipSynced is enabled
      let initialSkip: number | undefined = undefined;
      if (skipSyncedOption) {
        // Get total from Supabase (already synced patients)
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');
        const { count } = await supabase
          .from('patients')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', session.user.id);
        const existingCount = count || 0;
        
        // Calculate which page to start from: floor(existingCount / 100) * 100
        // This rounds down to the nearest page boundary
        const skipPages = Math.floor(existingCount / 100);
        initialSkip = skipPages * 100;
        
        toast.loading(`Starting from patient ${initialSkip} (skipping ${existingCount} existing)`, { id: progressToastId });
      }

      // Refresh token per request to avoid 401 during long runs
      let isLastPage = false;
      let logId: string | undefined;
      let totalEstimate: number | undefined;

      while (!isLastPage) {
        // 1) Fetch a page and stage it
        const s1 = await supabase.auth.getSession();
        const accessToken1 = s1.data.session?.access_token;
        const fetchRes = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/patients-fetch`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken1}`,
          },
          body: JSON.stringify({ logId, initialSkip }),
        });
        const fetchJson = await fetchRes.json();
        if (!fetchRes.ok) throw new Error(fetchJson.error || 'Fetch failed');

        logId = fetchJson.log_id as string;
        const batchId = fetchJson.batchId as string;
        isLastPage = !!fetchJson.isLastPage;
        totalEstimate = fetchJson.totalEstimate;
        const skip = fetchJson.skip || 0;
        const est = totalEstimate ? `/${totalEstimate}` : '';
        toast.loading(`Fetched ${fetchJson.itemsCount}. Processing ${skip}${est} (page ${Math.floor(skip/100)})`, { id: progressToastId });

        // 2) Process that staged batch
        const s2 = await supabase.auth.getSession();
        const accessToken2 = s2.data.session?.access_token;
        const procRes = await fetch(`${import.meta.env.VITE_SUPABASE_FUNCTIONS_URL}/patients-process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken2}`,
        },
          body: JSON.stringify({ logId, batchId }),
      });
        const procJson = await procRes.json();
        if (!procRes.ok) throw new Error(procJson.error || 'Process failed');

        // Refresh table data
        queryClient.invalidateQueries({ queryKey: ['patients-total-count'] });
        queryClient.invalidateQueries({ queryKey: ['patients-synced-count'] });
        queryClient.invalidateQueries({ queryKey: ['patients'] });
      }

      toast.success('Sync complete!', { id: progressToastId });
    } catch (err: any) {
      toast.error('Sync FROM 4D failed: ' + (err.message || err.error_description || 'Unknown error'));
    } finally {
      setSyncingFrom4D(false);
    }
  };

  // Sync TO ZoneDM: push patients as contacts in small batches (rate-limit safe), then push appointments for associations
  const syncToZoneDM = async (skip: boolean) => {
    if (syncingToZoneDM) return;
    setSyncingToZoneDM(true);
    const progressToastId = 'patients-push-progress';
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const userId = session.user.id;
      // Warm the lambda to reduce cold-start delays
      try { await fetch('/.netlify/functions/ping'); } catch {}

      // Determine initial pending count for progress - only count items that WILL be processed
      // skip=true means SKIP synced items, so count received + errors (retry failed ones too)
      // skip=false means INCLUDE synced items, so count everything
      let pendingTotal: number | undefined = undefined;
      try {
        let countQuery = supabase
          .from('patients')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId);
        
        if (skip) {
          // Skip synced, but count NEW records (received) AND errors that need retry (zonedm_error)
          countQuery = countQuery.in('zonedm_sync_status', ['received', 'zonedm_error']);
        } else {
          // Everything
          countQuery = countQuery.in('zonedm_sync_status', ['received', 'zonedm_error', 'zonedm_synced']);
        }
        
        const { count, error: countErr } = await countQuery;
        if (countErr) console.error('Count error:', countErr);
        pendingTotal = typeof count === 'number' ? count : undefined;
        console.log(`[syncToZoneDM] skip=${skip}, pendingTotal=${pendingTotal} (${skip ? 'NEW only' : 'ALL'})`);
      } catch (err) {
        console.error('Pending count calculation error:', err);
      }

      // Loop batches until server reports none left or daily limit pause
      // As NEW records are synced, their status changes from 'received' to 'zonedm_synced'
      // The next query automatically excludes them (when skip=true), so we progress through all NEW records
      let totalSuccess = 0;
      let totalErrors = 0;
      let processed = 0;
      let consecutiveZeroBatches = 0; // Track consecutive batches with 0 records processed
      toast.loading(
        `Pushing patients to ZoneDM: ${processed}/${pendingTotal ?? '?'}`,
        { id: progressToastId }
      );
      for (let i = 0; i < 1000; i++) { // increased cap for large datasets
        // Use GHL burst budget: 100 req / 10s. Each patient is ~2 API calls (tags + upsert),
        // so ~40-50 patients/batch is the practical max under the burst limit.
        const pushPatientsUrl =
          `/.netlify/functions/manual-push-patients?user_id=${encodeURIComponent(userId)}` +
          `&limit=50&concurrency=8&budget_ms=22000&skip_synced=${skip ? 'true' : 'false'}`;
        const res = await fetch(pushPatientsUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        let bodyText = '';
        try { bodyText = await res.text(); } catch {}
        let body: any = undefined;
        try { body = bodyText ? JSON.parse(bodyText) : undefined; } catch { body = undefined; }
        if (res.status === 429) {
          toast(`Paused by API daily limit. Resume later.`, { id: progressToastId });
          break;
        }
        if (!res.ok) throw new Error((body && (body.error || body.message)) || bodyText || 'Failed pushing patients');
        const batchSuccess = Number(body?.success || 0);
        const batchErrors = Number(body?.errors || 0);
        const batchTotal = batchSuccess + batchErrors;
        const bodyTotal = Number(body?.total ?? 0);
        
        // Break if no records were returned (total === 0)
        if (bodyTotal === 0) {
          console.log(`[syncToZoneDM] No more patients to process (total=0), breaking loop`);
          break;
        }
        
        // Break if no records were actually processed in this batch (prevents infinite loops)
        if (batchTotal === 0) {
          consecutiveZeroBatches++;
          console.log(`[syncToZoneDM] Batch processed 0 records (consecutive: ${consecutiveZeroBatches})`);
          // If we've had 3 consecutive batches with 0 processed, likely stuck in a loop
          if (consecutiveZeroBatches >= 3) {
            console.log(`[syncToZoneDM] Detected stuck loop after ${consecutiveZeroBatches} consecutive zero batches, breaking`);
            toast.error(`Sync stopped: detected stuck loop (no records processed in ${consecutiveZeroBatches} consecutive batches)`, { id: progressToastId });
            break;
          }
        } else {
          // Reset counter if we processed some records
          consecutiveZeroBatches = 0;
          totalSuccess += batchSuccess;
          totalErrors += batchErrors;
          // Increment processed count
          processed += batchTotal;
          
          // Break if we've processed all expected records (or exceeded them)
          if (pendingTotal !== undefined && processed >= pendingTotal) {
            console.log(`[syncToZoneDM] Processed ${processed} records, reached/exceeded expected total (${pendingTotal}), breaking loop`);
            break;
          }
        }
        
        queryClient.invalidateQueries({ queryKey: ['patients'] });
        queryClient.invalidateQueries({ queryKey: ['patients-synced-count'] });
        // Update progress toast - show actual progress, capped at total
        const displayProcessed = pendingTotal !== undefined ? Math.min(processed, pendingTotal) : processed;
        toast.loading(
          `Pushing patients to ZoneDM: ${displayProcessed}/${pendingTotal ?? '?'} • last batch ${batchSuccess} ok, ${batchErrors} err`,
          { id: progressToastId }
        );
        // Brief yield to UI — keep short so we stay near the 100/10s GHL burst budget
        await new Promise(r => setTimeout(r, 100));
      }

      toast.success(`Sync TO ZoneDM completed: ${totalSuccess} success, ${totalErrors} errors`, { id: progressToastId });
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      queryClient.invalidateQueries({ queryKey: ['patients-synced-count'] });
    } catch (err: any) {
      toast.error('Sync TO ZoneDM failed: ' + (err?.message || 'Unknown error'), { id: progressToastId });
    } finally {
      setSyncingToZoneDM(false);
    }
  };

  const asDisplay = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const formatDateTime = (value?: string | null): string => {
    if (!value) return '—';
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  };

  const fullName = (...parts: Array<unknown>): string =>
    parts
      .map((part) => (part == null ? '' : String(part).trim()))
      .filter(Boolean)
      .join(' ')
      .trim();

  const patientDetailSections = useMemo(() => {
    if (!selectedPatient) return [];

    const patient = selectedPatient;
    const patientName = fullName(patient.first_name, patient.last_name);
    const syncStatus = asDisplay(patient.zonedm_sync_status ?? patient.sync_status ?? 'received');

    const sections = [
      {
        title: 'Identity',
        rows: [
          { label: 'Full Name', primary: patientName || '—' },
          { label: 'Email', primary: asDisplay(patient.email) },
          { label: 'Phone', primary: asDisplay(patient.phone_primary) },
          { label: 'DOB', primary: asDisplay(patient.dob) },
          { label: 'Status', primary: asDisplay(patient.status) },
          { label: 'Gender', primary: asDisplay(patient.gender) },
        ],
      },
      {
        title: '4D Mapping',
        rows: [
          { label: '4D Patient ID', primary: asDisplay(patient.external_id) },
          { label: 'Account Number', primary: asDisplay(patient.account_number) },
          { label: 'Coordinator ID', primary: asDisplay(patient.coordinator_id) },
          { label: 'Profile Photo ID', primary: asDisplay(patient.profile_photo_id) },
          {
            label: 'Received/Updated From 4D',
            primary: formatDateTime(patient.external_updated_at),
            secondary: patient.external_updated_at ? `external_updated_at: ${patient.external_updated_at}` : undefined,
          },
        ],
      },
      {
        title: 'ZoneDM Mapping',
        rows: [
          {
            label: 'ZoneDM Sync Status',
            primary: syncStatus,
            secondary: patient.zonedm_sync_error ? `error: ${patient.zonedm_sync_error}` : undefined,
          },
          { label: 'ZoneDM Contact ID', primary: asDisplay(patient.zonedm_contact_id) },
        ],
      },
      {
        title: 'Internal DB',
        rows: [
          { label: 'Patient UUID', primary: asDisplay(patient.id) },
          { label: 'User UUID', primary: asDisplay(patient.user_id) },
          {
            label: 'Created At',
            primary: formatDateTime(patient.created_at),
            secondary: patient.created_at ? `created_at: ${patient.created_at}` : undefined,
          },
          {
            label: 'Updated At',
            primary: formatDateTime(patient.updated_at),
            secondary: patient.updated_at ? `updated_at: ${patient.updated_at}` : undefined,
          },
        ],
      },
      {
        title: 'Contact & Address',
        rows: [
          { label: 'Address Line 1', primary: asDisplay(patient.address1) },
          { label: 'Address Line 2', primary: asDisplay(patient.address2) },
          { label: 'City', primary: asDisplay(patient.city) },
          { label: 'State', primary: asDisplay(patient.state) },
          { label: 'Zipcode', primary: asDisplay(patient.zipcode) },
          { label: 'Country', primary: asDisplay(patient.country) },
        ],
      },
    ];

    return sections
      .map((section) => ({
        ...section,
        rows: section.rows.filter((row) => row.primary !== '—' || Boolean(row.secondary)),
      }))
      .filter((section) => section.rows.length > 0);
  }, [selectedPatient]);

  // Combine server-side paginated patients with CSV patients (CSV patients are always shown)
  const allPatients = useMemo(() => [
    ...patientsData,
    ...csvPatients.map((p, idx) => ({ ...p, id: 'csv-' + idx, isCsv: true })),
  ], [patientsData, csvPatients]);

  // Paginated patients are already server-side paginated, CSV patients are appended
  const paginatedPatients = allPatients;

  // Seed search from query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get('search');
    if (s) {
      const normalized = normalizeSearchInput(s);
      setSearchInput(normalized);
      setSearchTerm(normalized);
    }
  }, []);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      // Scroll to top of table when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  // Handle rows per page change
  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold">Patients</h1>
        <div className="flex-1 flex justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-700 font-medium">Search:</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); }}
              placeholder="Name, email, phone, status, account #, IDs"
              className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white min-w-[220px]"
            />
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div>Synced: {patientsSyncedCount.toLocaleString()}/{patientsTotalCount.toLocaleString()}</div>
          <button onClick={() => setConfirmSyncFrom4DOpen(true)} className="bg-blue-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[120px]" disabled={syncingFrom4D}>
            {syncingFrom4D ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Syncing...
              </>
            ) : (
              'Sync FROM 4D'
            )}
          </button>
          <button onClick={() => setConfirmOpen(true)} disabled={syncingToZoneDM} className={`bg-green-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[140px] ${syncingToZoneDM ? 'opacity-80 cursor-not-allowed' : ''}`}>
            {syncingToZoneDM ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                Syncing...
              </>
            ) : (
              'Sync TO ZoneDM'
            )}
          </button>
          <button
            onClick={async () => {
              try {
                setExportingCsv(true);
                const { data: { session } } = await supabase.auth.getSession();
                if (!session?.user?.id) throw new Error('Not authenticated');
                const userId = session.user.id;
                // Prefer server export (identity_only=true). On failure (e.g., 504), fall back to client-side export.
                const serverUrl = `/.netlify/functions/export-patients-csv?user_id=${encodeURIComponent(userId)}`;
                let usedServer = false;
                try {
                  const res = await fetch(serverUrl);
                  if (res.ok) {
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    const today = new Date();
                    const y = today.getFullYear();
                    const m = String(today.getMonth() + 1).padStart(2, '0');
                    const d = String(today.getDate()).padStart(2, '0');
                    a.href = url;
                    a.download = `patients-for-highlevel-${y}${m}${d}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    usedServer = true;
                    toast.success('CSV exported');
                  }
                } catch {}
                if (!usedServer) {
                  // Client-side export respecting current filters; exports ALL rows
                  function toYmd(dateLike?: string | null) {
                    if (!dateLike) return '';
                    const d = new Date(dateLike);
                    if (isNaN(d.getTime())) return '';
                    const yyyy = d.getFullYear();
                    const mm = String(d.getMonth() + 1).padStart(2, '0');
                    const dd = String(d.getDate()).padStart(2, '0');
                    return `${yyyy}-${mm}-${dd}`;
                  }
                  function normalizeCountry(country?: string | null) {
                    if (!country) return '';
                    const c = String(country).trim();
                    if (!c) return '';
                    const lower = c.toLowerCase();
                    if (c.length === 2) return c.toUpperCase();
                    if (lower.includes('united') || lower.includes('u.s.') || lower === 'usa' || lower === 'us') return 'US';
                    if (lower.includes('canada') || lower === 'ca') return 'CA';
                    return c;
                  }
                  function isValidEmail(email?: string | null) {
                    if (!email) return false;
                    const v = String(email).trim();
                    if (!v || v.toLowerCase() === 'no@email') return false;
                    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
                  }
                  function escapeCsv(value: any) {
                    const s = value == null ? '' : String(value);
                    if (s === '') return '';
                    const needsQuotes = /[\",\\n\\r]/.test(s);
                    const sanitized = s.replace(/\"/g, '\"\"');
                    return needsQuotes ? `\"${sanitized}\"` : sanitized;
                  }
                  const headers = [
                    'First Name',
                    'Last Name',
                    'Email',
                    'Phone',
                    'Date of Birth',
                    'Address 1',
                    'City',
                    'State',
                    'Postal Code',
                    'Country',
                    'Tags',
                    'PatientId',
                    'Gender',
                    'Source',
                  ];
                  const lines: string[] = [];
                  lines.push(headers.join(','));
                  // Stream ALL patients in pages (ignore UI filters to guarantee full dump)
                  const pageSize = 1000;
                  let from = 0;
                  let total = 0;
                  let firstToast = true;
                  while (true) {
                    const { data: rows, error, count } = await supabase
                      .from('patients')
                      .select('external_id, first_name, last_name, email, phone_primary, dob, address1, city, state, zipcode, country, gender', { count: 'exact' })
                      .eq('user_id', userId)
                      .order('last_name', { ascending: true })
                      .range(from, from + pageSize - 1);
                    if (error) throw error;
                    total = count || total;
                    const batch = rows || [];
                    for (const p of batch) {
                      const firstName = p.first_name || '';
                      const lastName = p.last_name || '';
                      const email = isValidEmail(p.email) ? p.email : '';
                      const phone = p.phone_primary || '';
                      const dob = toYmd(p.dob);
                      const address1 = p.address1 || '';
                      const city = p.city || '';
                      const state = p.state || '';
                      const postal = p.zipcode || '';
                      const country = normalizeCountry(p.country);
                      const tags = 'Patient';
                      const patientId = p.external_id == null ? '' : String(p.external_id);
                      const gender = p.gender || '';
                      const source = '4D EMR CSV Import';
                      const row = [
                        firstName, lastName, email, phone, dob, address1, city, state, postal, country, tags, patientId, gender, source,
                      ].map(escapeCsv).join(',');
                      lines.push(row);
                    }
                    const processed = Math.min(from + (rows?.length || 0), total || from + (rows?.length || 0));
                    if (firstToast) {
                      toast.loading(`Exporting CSV... ${processed}/${total || '?'}`, { id: 'patients-export' });
                      firstToast = false;
                    } else {
                      toast.loading(`Exporting CSV... ${processed}/${total || '?'}`, { id: 'patients-export' });
                    }
                    if (batch.length < pageSize) break;
                    from += pageSize;
                  }
                  const csv = lines.join('\r\n') + '\r\n';
                  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  const today = new Date();
                  const y = today.getFullYear();
                  const m = String(today.getMonth() + 1).padStart(2, '0');
                  const d = String(today.getDate()).padStart(2, '0');
                  a.href = url;
                  a.download = `patients-for-highlevel-${y}${m}${d}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('CSV exported', { id: 'patients-export' });
                }
              } catch (e: any) {
                toast.error(e?.message || 'Export failed');
              } finally {
                setExportingCsv(false);
              }
            }}
            disabled={exportingCsv}
            className={`bg-indigo-600 text-white px-4 py-2 rounded flex items-center justify-center min-w-[180px] ${exportingCsv ? 'opacity-80 cursor-not-allowed' : ''}`}
          >
            {exportingCsv ? 'Exporting…' : 'Export CSV for HighLevel'}
          </button>
          <label className="bg-gray-200 px-4 py-2 rounded cursor-pointer">
            Upload CSV
            <input type="file" accept=".csv" className="hidden" onChange={handleCsvUpload} />
          </label>
        </div>
      </div>
      <Card>
        <div className="overflow-x-auto p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-gray-50 border border-gray-200 rounded-md px-4 py-3 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Date Range:</label>
              <select
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value as 'all' | 'recent'); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="recent">Last 3 Months</option>
                <option value="all">All Records</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">Sort:</label>
              <select
                value={sortOrder}
                onChange={(e) => { setSortOrder(e.target.value as 'newest' | 'oldest'); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 font-medium">ZoneDM Status:</label>
              <select
                value={syncStatusFilter}
                onChange={(e) => { setSyncStatusFilter(e.target.value); setPage(1); }}
                className="border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-2 py-1 bg-white"
              >
                <option value="all">All Statuses</option>
                <option value="received">Received</option>
                <option value="zonedm_synced">Synced</option>
                <option value="zonedm_error">Error</option>
                <option value="missing_contact">Missing Contact</option>
                <option value="empty">Empty</option>
              </select>
            </div>
            <div className="text-sm text-gray-500 font-medium">
              Page {page} of {totalPages} ({patientsCount.toLocaleString()} total)
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200 text-xs shadow-sm border border-gray-200 rounded-md bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email / Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DOB</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">4D Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ZoneDM Sync</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedPatients.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                    No patients found.
                  </td>
                </tr>
              )}
              {paginatedPatients.map((p: any) => (
                <tr
                  key={p.id}
                  className={p.isCsv ? "border-t bg-blue-50 hover:bg-blue-100 cursor-pointer" : "border-t hover:bg-gray-50 cursor-pointer"}
                  onClick={() => { setSelectedPatient(p); setIsModalOpen(true); }}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-700 underline">
                    <Link to={`/dashboard/patients?search=${encodeURIComponent(String(p.last_name ?? ''))}`}>
                      {p.last_name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-700 underline">
                    <Link to={`/dashboard/appointments?search=${encodeURIComponent(String(p.external_id ?? p.id))}`}>
                      {p.first_name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {p.email && (
                      <div>{p.email}</div>
                    )}
                    {p.phone_primary && (
                      <div className="text-gray-400">{p.phone_primary}</div>
                    )}
                    {!p.email && !p.phone_primary && '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{p.dob}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {p.isCsv ? (
                      <span className="text-gray-500">CSV Upload</span>
                    ) : (
                      p.status || '—'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {(() => {
                      const status = (p.zonedm_sync_status ?? p.sync_status) as string | undefined;
                      const errorMsg = (p.zonedm_sync_error as string | undefined) || undefined;
                      const base = 'px-2 inline-flex text-xs leading-5 font-semibold rounded-full';
                      const cls = !status || status === 'received'
                        ? 'bg-yellow-100 text-yellow-800'
                        : status === 'zonedm_synced' || status === 'synced'
                        ? 'bg-green-100 text-green-800'
                        : status === 'zonedm_error'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800';
                      const pill = (
                        <span className={`${base} ${cls}`}>{status || 'received'}</span>
                      );
                      if (status === 'zonedm_error' && errorMsg) {
                        return (
                          <button
                            type="button"
                            className="underline decoration-dotted decoration-red-400"
                            onClick={(e) => { e.stopPropagation(); toast.error(errorMsg); }}
                            title={errorMsg}
                          >
                            {pill}
                          </button>
                        );
                      }
                      return pill;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-6">
            <button
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
            <div className="flex flex-wrap gap-1 justify-center">
              {(() => {
                // Show max 10 page buttons at a time, centered around current page
                const maxButtons = 10;
                let startPage = Math.max(1, page - Math.floor(maxButtons / 2));
                let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                
                // Adjust if we're near the end
                if (endPage - startPage < maxButtons - 1) {
                  startPage = Math.max(1, endPage - maxButtons + 1);
                }
                
                const pages: (number | string)[] = [];
                
                // Add first page if not in range
                if (startPage > 1) {
                  pages.push(1);
                  if (startPage > 2) pages.push('...');
                }
                
                // Add page numbers
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(i);
                }
                
                // Add last page if not in range
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) pages.push('...');
                  pages.push(totalPages);
                }
                
                return pages.map((pNum, idx) => 
                  pNum === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-gray-500">...</span>
                  ) : (
                    <button
                      key={pNum}
                      className={`px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none ${pNum === page ? 'bg-blue-100 font-bold border-blue-400' : ''}`}
                      onClick={() => handlePageChange(pNum as number)}
                      disabled={pNum === page}
                    >
                      {pNum}
                    </button>
                  )
                );
              })()}
            </div>
            <button
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </Card>
      {/* Patient Details Modal */}
      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setIsModalOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold mb-4">Patient Details</h2>
            {selectedPatient && (
              <div className="max-h-[70vh] overflow-y-auto pr-2">
                {patientDetailSections.map((section) => (
                  <div key={section.title} className="mb-6">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">{section.title}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                      {section.rows.map((row) => (
                        <React.Fragment key={`${section.title}-${row.label}`}>
                          <div className="text-right font-semibold text-gray-700 truncate pr-2 py-1 border-b border-gray-100">
                            {row.label}
                          </div>
                          <div className="text-left text-gray-900 break-all py-1 border-b border-gray-100">
                            <div>{row.primary}</div>
                            {row.secondary && (
                              <div className="text-xs text-gray-500 font-mono mt-1">{row.secondary}</div>
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirm Sync Modal */}
      {/* Confirm Sync FROM 4D Modal */}
      <Dialog open={confirmSyncFrom4DOpen} onClose={() => setConfirmSyncFrom4DOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setConfirmSyncFrom4DOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setConfirmSyncFrom4DOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm Sync FROM 4D</Dialog.Title>
            <div className="mt-3 text-sm text-gray-600">
              This will fetch patients from 4D EMR and sync them to Supabase. The sync will paginate through all available patients (100 per page).
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={skipSyncedFrom4D}
                onChange={(e) => setSkipSyncedFrom4D(e.target.checked)}
              />
              <span>Skip synced patients (start from next page after existing sync)</span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmSyncFrom4DOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                onClick={async () => {
                  setConfirmSyncFrom4DOpen(false);
                  await syncFrom4D(skipSyncedFrom4D);
                }}
                disabled={syncingFrom4D}
              >
                Confirm
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Confirm Sync TO ZoneDM Modal */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" onClick={() => setConfirmOpen(false)} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-xl shadow-lg p-6 relative">
            <button
              onClick={() => setConfirmOpen(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
            <Dialog.Title className="text-lg font-semibold text-gray-900">Confirm Sync TO ZoneDM</Dialog.Title>
            <div className="mt-3 text-sm text-gray-600">
              This will push patients to ZoneDM in rate-limit-safe batches (~50 per request, using HighLevel&apos;s 100 req / 10s burst).
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
                checked={skipSynced}
                onChange={(e) => setSkipSynced(e.target.checked)}
              />
              <span>Skip synced patients (zonedm_synced)</span>
            </label>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
                onClick={async () => { setConfirmOpen(false); await syncToZoneDM(skipSynced); }}
                disabled={syncingToZoneDM}
              >
                Confirm
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
} 